import React, { useEffect, useState } from "react";
import { colors } from "../../constants/GlobalStyles";
import { IconCarotArrowRight, IconCarotArrowDown } from "../../icons";
import "../../App.css";
//chakra button
import { Button as ChakraButton, ButtonProps as ChakraButtonProps, Text, TextProps, useTheme } from "@chakra-ui/react";
import GlowingRaysButtonEffectImage from "./GlowingRaysButtonEffectImage.png";

type ButtonPrimaryOrSecondaryType = "primary" | "secondary";
type ButtonStyleType = "default" | "outline" | "light" | "text" | "ghost";
type ButtonSizeType = "large" | "medium" | "small";

type ButtonProps = ChakraButtonProps & {
  buttonPrimaryOrSecondaryType?: ButtonPrimaryOrSecondaryType;
  buttonStyleType?: ButtonStyleType;
  size?: ButtonSizeType;
  onClick?: any;
  disabled?: boolean;
  hasLeftCarotArrowDropDown?: boolean;
  hasRightCarotArrow?: boolean;
  title: string;
  style?: any;
  pressed?: boolean;
  // just pass in an icon like this "LeftIcon={IconCards}"
  LeftIcon?: (props: any) => JSX.Element;
  RightIcon?: (props: any) => JSX.Element;
  iconColor?: string;
  widthIcon?: string;
  heightIcon?: string;
  iconSpacing?: string;
  ref?: any;
  buttonTitleTextProps?: TextProps;
  shouldAllowTitleToWrap?: boolean;
  shouldAddButtonGlowingRaysEffect?: boolean;
  isRoundButton?: boolean;
  pressedBackgroundColor?: string;
  pressedHoverBackgroundColor?: string;
  // Allow using the button as a react-router Link via `as={RouterLink}`
  to?: string;
};

export default function Button({
  buttonPrimaryOrSecondaryType = "primary",
  buttonStyleType = "default",
  size = "large",
  onClick,
  pressed = false,
  hasLeftCarotArrowDropDown = false,
  hasRightCarotArrow = false,
  title = "-",
  disabled = false,
  style,
  LeftIcon,
  RightIcon,
  iconColor,
  iconSpacing,
  buttonTitleTextProps,
  shouldAllowTitleToWrap = false,
  shouldAddButtonGlowingRaysEffect = false,
  isRoundButton = false,
  pressedBackgroundColor,
  pressedHoverBackgroundColor,
  widthIcon,
  heightIcon,
  ...props
}: ButtonProps): JSX.Element {
  const isRightToLeft = useTheme().direction === "rtl";
  const arrowStyle = { transform: isRightToLeft ? "scaleX(-1)" : undefined };
  const [isHovered, setIsHovered] = useState(false);
  const [isActive, setIsActive] = useState(false);

  const onClickAction = (event) => {
    if (!disabled && onClick) {
      onClick(event);
    }
  };

  useEffect(() => {
    const handleMouseUp = () => {
      setIsActive(false);
    };

    // Add a global event listener
    window.addEventListener("mouseup", handleMouseUp);

    // Cleanup the global event listener
    return () => {
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  let fontSize = "20px";
  let lineHeight = "32px";
  if (size === "medium") {
    fontSize = "16px";
    lineHeight = "24px";
  }
  if (size === "small") {
    fontSize = "14px";
    lineHeight = "16px";
  }

  let textAndIconColor = null;

  if (buttonStyleType === "default") {
    textAndIconColor = colors.text.white;
    if (disabled) {
      textAndIconColor =
        buttonPrimaryOrSecondaryType === "primary"
          ? colors.primaryAction.disabledLabel
          : colors.secondaryAction.disabledLabel;
    }
  }

  if (buttonStyleType === "ghost" || buttonStyleType === "text") {
    textAndIconColor =
      buttonPrimaryOrSecondaryType === "primary" ? colors.primaryAction.default : colors.secondaryAction.default;

    if (isHovered) {
      textAndIconColor =
        buttonPrimaryOrSecondaryType === "primary" ? colors.primaryAction.hover : colors.secondaryAction.hover;
      if (props?._hover) {
        textAndIconColor = props?._hover?.textColor;
      }
    }
    if (isActive || pressed) {
      textAndIconColor = colors.primaryAction.pressed;
    }
    if (disabled) {
      textAndIconColor =
        buttonPrimaryOrSecondaryType === "primary"
          ? colors.primaryAction.inactive
          : colors.secondaryAction.inactiveDarker;
    }
  }

  if (buttonStyleType === "outline") {
    textAndIconColor =
      buttonPrimaryOrSecondaryType === "primary" ? colors.primaryAction.default : colors.secondaryAction.default;

    if (isHovered) {
      textAndIconColor =
        buttonPrimaryOrSecondaryType === "primary" ? colors.primaryAction.hover : colors.secondaryAction.hover;
    }
    if (isActive || pressed) {
      textAndIconColor =
        buttonPrimaryOrSecondaryType === "primary" ? colors.primaryAction.pressed : colors.secondaryAction.pressed;
    }
    if (disabled) {
      textAndIconColor =
        buttonPrimaryOrSecondaryType === "primary" ? colors.primaryAction.inactive : colors.secondaryAction.inactive;
    }
  }

  if (buttonStyleType === "light") {
    textAndIconColor =
      buttonPrimaryOrSecondaryType === "primary" ? colors.primaryAction.hover : colors.secondaryAction.default;

    if (isHovered || isActive || pressed) {
      textAndIconColor =
        buttonPrimaryOrSecondaryType === "primary" ? colors.primaryAction.pressed : colors.secondaryAction.pressed;
    }
    if (disabled) {
      textAndIconColor =
        buttonPrimaryOrSecondaryType === "primary" ? colors.primaryAction.inactive : colors.secondaryAction.inactive;
    }
  }

  if (buttonStyleType === "ghost") {
    return (
      <ChakraButton
        height={isRoundButton ? "44px" : "auto"}
        lineHeight={lineHeight}
        display="flex"
        padding={isRoundButton ? "0px" : "12px 24px"}
        width={isRoundButton ? "44px" : "auto"}
        minWidth={isRoundButton ? "44px" : "auto"}
        borderRadius="100px"
        justifyContent={isRoundButton ? "center" : "flex-start"}
        border={"none"}
        isDisabled={disabled}
        style={style}
        bg={isActive || pressed ? (pressedBackgroundColor ? pressedBackgroundColor : "transparent") : "transparent"}
        color={textAndIconColor}
        fontSize={fontSize}
        fontWeight="500"
        _hover={
          !disabled
            ? {
                cursor: "pointer",
                bg:
                  pressed && pressedHoverBackgroundColor
                    ? pressedHoverBackgroundColor
                    : buttonPrimaryOrSecondaryType === "primary"
                      ? colors.primaryAction.light2
                      : colors.secondaryAction.light,
              }
            : { cursor: "not-allowed" }
        }
        _active={!disabled ? { bg: pressedBackgroundColor ? pressedBackgroundColor : "transparent" } : undefined}
        _disabled={{
          cursor: "not-allowed",
        }}
        _focusVisible={{ border: "none" }}
        _focus={{
          boxShadow: "none",
          outline: "none",
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onMouseDown={() => setIsActive(true)}
        onMouseUp={() => setIsActive(false)}
        onClick={onClickAction}
        // Ensure child elements cannot intercept events
        sx={{
          "*": {
            pointerEvents: "none",
          },
        }}
        {...props}
      >
        <span style={{ pointerEvents: "none", display: "inline-flex", alignItems: "center" }}>
          {hasLeftCarotArrowDropDown &&
            !isRoundButton &&
            (!pressed ? <IconCarotArrowRight style={arrowStyle} /> : <IconCarotArrowDown color={textAndIconColor} />)}
          {LeftIcon && (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                marginInlineEnd: !isRoundButton ? (iconSpacing ? iconSpacing : "12px") : undefined,
              }}
            >
              <LeftIcon
                width={isRoundButton ? "24" : "16"}
                height={isRoundButton ? "24" : "16"}
                color={textAndIconColor}
              />
            </span>
          )}
          {!isRoundButton && (
            <Text color={textAndIconColor} {...buttonTitleTextProps}>
              {title}
            </Text>
          )}
          {RightIcon && (
            <span
              style={{ display: "inline-flex", alignItems: "center", marginInlineStart: iconSpacing ? iconSpacing : "12px" }}
            >
              <RightIcon width="16px" height="16" color={textAndIconColor} />
            </span>
          )}
          {hasRightCarotArrow && !isRoundButton && <IconCarotArrowRight style={arrowStyle} />}
        </span>
      </ChakraButton>
    );
  }
  if (buttonStyleType === "text") {
    return (
      <ChakraButton
        height="auto"
        lineHeight={lineHeight}
        display="flex"
        justifyContent={title?.length > 0 ? "flex-start" : "center"}
        border={"none"}
        isDisabled={disabled}
        style={style}
        bg={isActive || pressed ? (pressedBackgroundColor ? pressedBackgroundColor : "transparent") : "transparent"}
        color={textAndIconColor}
        fontSize={fontSize}
        fontWeight="500"
        padding="0px"
        _hover={
          !disabled
            ? {
                cursor: "pointer",
                textDecoration: "underline",
              }
            : { cursor: "not-allowed" }
        }
        _active={!disabled ? { bg: pressedBackgroundColor ? pressedBackgroundColor : "transparent" } : undefined}
        _disabled={{
          cursor: "not-allowed",
        }}
        _focusVisible={{ border: "none", outline: "none", bg: "transparent" }}
        _focus={{
          boxShadow: "none",
          outline: "none",
          bg: "transparent",
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onMouseDown={() => setIsActive(true)}
        onMouseUp={() => setIsActive(false)}
        onClick={onClickAction}
        sx={{ "*": { pointerEvents: "none" } }}
        {...props}
      >
        {hasLeftCarotArrowDropDown &&
          (!pressed ? <IconCarotArrowRight style={arrowStyle} /> : <IconCarotArrowDown color={textAndIconColor} />)}
        {LeftIcon && (
          <LeftIcon
            width={widthIcon ? widthIcon : size === "small" ? "20" : "24"}
            height={heightIcon ? heightIcon : size === "small" ? "20" : "24"}
            color={textAndIconColor}
            style={title?.length > 0 ? { marginInlineEnd: iconSpacing ? iconSpacing : "12px" } : undefined}
          />
        )}
        <Text color={textAndIconColor} {...buttonTitleTextProps}>
          {title}
        </Text>
        {RightIcon && (
          <RightIcon
            width={widthIcon ? widthIcon : size === "small" ? "20" : "24"}
            height={heightIcon ? heightIcon : size === "small" ? "20" : "24"}
            color={textAndIconColor}
            style={{ marginInlineStart: iconSpacing ? iconSpacing : "12px" }}
          />
        )}
        {hasRightCarotArrow && <IconCarotArrowRight style={arrowStyle} color={textAndIconColor} />}
      </ChakraButton>
    );
  }

  if (buttonStyleType === "outline") {
    return (
      <ChakraButton
        height="auto"
        lineHeight={lineHeight}
        display="flex"
        justifyContent="flex-start"
        isDisabled={disabled}
        style={style}
        bg="transparent"
        color={textAndIconColor}
        border={`1.5px solid ${
          !disabled && !isHovered && !isActive && !pressed
            ? buttonPrimaryOrSecondaryType === "primary"
              ? colors.primaryAction.outlineBorder
              : colors.secondaryAction.outlineBorder
            : textAndIconColor
        }`}
        fontSize={fontSize}
        borderRadius="100px"
        padding="10.5px 22.5px"
        fontWeight="500"
        isActive={pressed}
        _hover={{
          bg: buttonPrimaryOrSecondaryType === "primary" ? colors.primaryAction.light2 : colors.secondaryAction.light,
          border:
            buttonPrimaryOrSecondaryType === "primary"
              ? `1.5px solid ${colors.primaryAction.hover}`
              : `1.5px solid ${colors.secondaryAction.hover}`,
          cursor: "pointer",
        }}
        _active={
          !disabled
            ? {
                bg:
                  buttonPrimaryOrSecondaryType === "primary"
                    ? colors.primaryAction.inactive
                    : colors.secondaryAction.inactive,
              }
            : undefined
        }
        _disabled={{
          bg: "transparent",
          border:
            buttonPrimaryOrSecondaryType === "primary"
              ? `1.5px solid ${colors.primaryAction.inactive}`
              : `1.5px solid ${colors.secondaryAction.inactive}`,
          cursor: "not-allowed",
        }}
        _focusVisible={{ border: "none" }}
        _focus={{
          boxShadow: "none",
          outline: "none",
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onMouseDown={() => setIsActive(true)}
        onMouseUp={() => setIsActive(false)}
        onClick={onClickAction}
        sx={{ "*": { pointerEvents: "none" } }}
        {...props}
      >
        {LeftIcon && (
          <LeftIcon
            width="16"
            height="16"
            color={textAndIconColor}
            style={{ marginInlineEnd: iconSpacing ? iconSpacing : "12px" }}
          />
        )}
        <Text color={textAndIconColor} {...buttonTitleTextProps}>
          {title}
        </Text>
        {RightIcon && <RightIcon width="16px" height="16" color={textAndIconColor} />}
      </ChakraButton>
    );
  }

  if (buttonStyleType === "light") {
    return (
      <ChakraButton
        height={isRoundButton ? "44px" : "auto"}
        lineHeight={lineHeight}
        border={"none"}
        isDisabled={disabled}
        gap={iconSpacing ? iconSpacing : "12px"}
        color={textAndIconColor}
        borderRadius="100px"
        width={isRoundButton ? "44px" : "auto"}
        minWidth={isRoundButton ? "44px" : "auto"}
        bg={buttonPrimaryOrSecondaryType === "primary" ? colors.primaryAction.light2 : colors.secondaryAction.light}
        p={isRoundButton ? "0px" : "12px 24px"}
        fontSize={fontSize}
        fontWeight="500"
        _hover={{
          bg:
            buttonPrimaryOrSecondaryType === "primary"
              ? colors.primaryAction.inactive
              : colors.secondaryAction.inactive,

          cursor: "pointer",
        }}
        _active={
          !disabled
            ? {
                bg:
                  buttonPrimaryOrSecondaryType === "primary"
                    ? colors.primaryAction.light2
                    : colors.secondaryAction.light,
              }
            : undefined
        }
        _focusVisible={{ border: "none" }}
        _disabled={{
          color: textAndIconColor,
          bg: buttonPrimaryOrSecondaryType === "primary" ? colors.primaryAction.light2 : colors.secondaryAction.light,
          cursor: "not-allowed",
        }}
        style={style}
        _focus={{
          boxShadow: "none",
          outline: "none",
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onMouseDown={() => setIsActive(true)}
        onMouseUp={() => setIsActive(false)}
        onClick={onClickAction}
        sx={{ "*": { pointerEvents: "none" } }}
        {...props}
      >
        {LeftIcon && (
          <LeftIcon width={isRoundButton ? "24" : "16"} height={isRoundButton ? "24" : "16"} color={textAndIconColor} />
        )}
        {!isRoundButton && (
          <Text
            color={textAndIconColor}
            {...buttonTitleTextProps}
            {...(shouldAllowTitleToWrap
              ? { noOfLines: undefined, whiteSpace: "normal", textAlign: "center", flex: "1 1 0%", minW: 0 }
              : {})}
          >
            {title}
          </Text>
        )}
        {RightIcon && !isRoundButton && <RightIcon width="24" height="24" color={textAndIconColor} />}
      </ChakraButton>
    );
  }

  // if it is not a text button it will be a regular button (buttonStyleType === "default")
  return (
    <ChakraButton
      height="auto"
      lineHeight={lineHeight}
      border={"none"}
      isDisabled={disabled}
      gap={iconSpacing ? iconSpacing : "12px"}
      // The label is white, so the background is a fill color: it stays dark enough in both themes
      bg={
        buttonPrimaryOrSecondaryType === "primary"
          ? pressed
            ? colors.primaryAction.fillPressed
            : colors.primaryAction.fill
          : pressed
            ? colors.secondaryAction.fillPressed
            : colors.secondaryAction.fill
      }
      backgroundImage={shouldAddButtonGlowingRaysEffect ? `url(${GlowingRaysButtonEffectImage})` : undefined}
      backgroundColor={shouldAddButtonGlowingRaysEffect ? colors.primaryAction.heroFill : undefined}
      backgroundPosition={shouldAddButtonGlowingRaysEffect ? "center" : undefined}
      backgroundRepeat={shouldAddButtonGlowingRaysEffect ? "no-repeat" : undefined}
      backgroundSize={shouldAddButtonGlowingRaysEffect ? "cover" : undefined}
      color={textAndIconColor}
      borderRadius="100px"
      p="12px 24px"
      fontSize={fontSize}
      fontWeight="500"
      _hover={
        !pressed && {
          cursor: "pointer",
          backgroundImage: shouldAddButtonGlowingRaysEffect ? `url(${GlowingRaysButtonEffectImage})` : undefined,
          backgroundColor: shouldAddButtonGlowingRaysEffect
            ? colors.primaryAction.heroFillHover
            : buttonPrimaryOrSecondaryType === "primary"
              ? colors.primaryAction.fillHover
              : colors.secondaryAction.fillHover,
          backgroundPosition: shouldAddButtonGlowingRaysEffect ? "center" : undefined,
          backgroundRepeat: shouldAddButtonGlowingRaysEffect ? "no-repeat" : undefined,
          backgroundSize: shouldAddButtonGlowingRaysEffect ? "cover" : undefined,
          boxShadow: shouldAddButtonGlowingRaysEffect ? "0px 8px 20px 0px rgba(26, 172, 80, 0.16)" : undefined,
        }
      }
      _disabled={{
        bg:
          buttonPrimaryOrSecondaryType === "primary"
            ? colors.primaryAction.disabledFill
            : colors.secondaryAction.disabledFill,
        cursor: "not-allowed",
      }}
      _active={
        !disabled
          ? {
              bg:
                buttonPrimaryOrSecondaryType === "primary"
                  ? colors.primaryAction.fillPressed
                  : colors.secondaryAction.fillPressed,
            }
          : undefined
      }
      _focusVisible={{ border: "none" }}
      style={style}
      _focus={{
        boxShadow: "none",
        outline: "none",
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onMouseDown={() => setIsActive(true)}
      onMouseUp={() => setIsActive(false)}
      onClick={onClickAction}
      sx={{ "*": { pointerEvents: "none" } }}
      {...props}
    >
      {LeftIcon && <LeftIcon width="24" height="24" color={textAndIconColor} />}
      <Text
        color={textAndIconColor}
        {...buttonTitleTextProps}
        noOfLines={shouldAllowTitleToWrap ? undefined : 1}
        whiteSpace={shouldAllowTitleToWrap ? "normal" : undefined}
        textAlign={shouldAllowTitleToWrap ? "center" : undefined}
        flex={shouldAllowTitleToWrap ? "1 1 0%" : undefined}
        minW={shouldAllowTitleToWrap ? 0 : undefined}
      >
        {title}
      </Text>
      {RightIcon && <RightIcon width="24" height="24" color={textAndIconColor} />}
    </ChakraButton>
  );
}
